import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    Grid,
    InputAdornment,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
} from '@mui/material';
import { Close, Edit, Delete } from '@mui/icons-material';
import DayPricesEditor from './DayPricesEditor';
import { businessAPI } from '../api';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const EMPTY_FORM = {
    base_price: '',
    f_effective_from: new Date().toISOString().slice(0, 10),
    f_reason: '',
    day_prices: {},
};

const ProductPriceDialog = ({ open, onClose, product, onProductUpdated }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [priceHistory, setPriceHistory] = useState([]);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [editingId, setEditingId] = useState(null);

    const billingType = product?.f_billing_type || product?.product_billing_type;
    const isMonthly = billingType === 'MonthlyFlatFee';
    const billingLabel = isMonthly ? 'Monthly Rate' : 'Per Item';

    const loadHistory = useCallback(async () => {
        if (!product?.f_id) return;
        setLoading(true);
        try {
            const res = await businessAPI.getPriceHistory(product.f_id);
            setPriceHistory(res.data || []);
        } catch (err) {
            console.error('Failed to load price history:', err);
        } finally {
            setLoading(false);
        }
    }, [product?.f_id]);

    useEffect(() => {
        if (open && product?.f_id) {
            loadHistory();
            setForm({ ...EMPTY_FORM, f_effective_from: new Date().toISOString().slice(0, 10) });
            setEditingId(null);
            setError('');
            setSuccess('');
        }
    }, [open, product?.f_id, loadHistory]);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, [open]);

    const formatError = (err) => {
        if (Array.isArray(err.response?.data?.detail)) return err.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        if (typeof err.response?.data?.detail === 'string') return err.response.data.detail;
        return err.message || 'An error occurred';
    };

    const handleStartEdit = (log) => {
        setEditingId(log.f_id);
        setForm({
            base_price: log.f_price ? (parseFloat(log.f_price) || '').toString() : '',
            f_effective_from: log.f_effective_from?.slice(0, 10) || new Date().toISOString().slice(0, 10),
            f_reason: log.f_reason || '',
            day_prices: log.day_prices ? { ...log.day_prices } : {},
        });
        setError('');
        setSuccess('');
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setForm({ ...EMPTY_FORM, f_effective_from: new Date().toISOString().slice(0, 10) });
    };

    const handleSave = async () => {
        if (!form.base_price) { setError('Base price is required'); return; }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const dayPrices = {};
            Object.entries(form.day_prices || {}).forEach(([day, price]) => {
                if (price !== '' && price !== null) dayPrices[day] = parseFloat(price);
            });
            const data = {
                f_effective_from: form.f_effective_from,
                f_reason: form.f_reason,
                base_price: parseFloat(form.base_price),
                day_prices: Object.keys(dayPrices).length > 0 ? dayPrices : null,
            };
            // Edit = delete old entry + post new (no PUT endpoint)
            if (editingId) {
                await businessAPI.deletePriceLog(product.f_id, editingId);
            }
            const res = await businessAPI.addPriceLog(product.f_id, data);
            const ap = res.data.affected_locked_periods;
            let msg = editingId ? 'Price updated.' : 'Price added.';
            if (ap?.length > 0) msg += ` ⚠️ Affects ${ap.length} locked period(s).`;
            setSuccess(msg);
            setEditingId(null);
            setForm({ ...EMPTY_FORM, f_effective_from: new Date().toISOString().slice(0, 10) });
            await loadHistory();
            if (onProductUpdated) onProductUpdated();
        } catch (err) {
            setError(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (priceLogId) => {
        if (!window.confirm('Delete this price entry?')) return;
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const res = await businessAPI.deletePriceLog(product.f_id, priceLogId);
            const ap = res.data.affected_locked_periods;
            let msg = 'Price entry deleted.';
            if (ap?.length > 0) msg += ` ⚠️ Affects ${ap.length} locked period(s).`;
            setSuccess(msg);
            if (editingId === priceLogId) handleCancelEdit();
            await loadHistory();
            if (onProductUpdated) onProductUpdated();
        } catch (err) {
            setError(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    const currentPrice = priceHistory.length > 0 ? priceHistory[0] : null;

    if (!product) return null;

    return (
        <Dialog open={open} onClose={onClose} {...APP_DIALOG}>
            <DialogTitle sx={APP_DIALOG_TITLE_SX}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h6">₹ Price</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.85 }}>{product.f_name}</Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ mt: 0.5, color: 'white' }}>
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

                {/* Current price summary */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip label={billingLabel} size="small" variant="outlined" color={isMonthly ? 'secondary' : 'default'} />
                    {currentPrice
                        ? <Chip label={`Current: ₹${currentPrice.f_price}`} size="small" color="primary" />
                        : <Typography variant="body2" color="text.disabled">No price set yet</Typography>
                    }
                </Box>

                {/* Add / Edit form */}
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2, mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                        {editingId ? '✏️ Edit Entry' : '+ New Price'}
                    </Typography>
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth label="Base Price *" type="number" size="small"
                                inputProps={{ step: 0.01, inputMode: 'decimal' }}
                                value={form.base_price}
                                onChange={(e) => setForm({ ...form, base_price: e.target.value })}
                                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                helperText={isMonthly ? 'Monthly rate' : 'All days'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth label="Effective From" type="date" size="small"
                                value={form.f_effective_from}
                                onChange={(e) => setForm({ ...form, f_effective_from: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        {!isMonthly && (
                            <Grid item xs={12}>
                                <DayPricesEditor
                                    value={form.day_prices || {}}
                                    onChange={(dp) => setForm({ ...form, day_prices: dp })}
                                    basePrice={form.base_price}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained" size="small"
                                    onClick={handleSave}
                                    disabled={loading || !form.base_price}
                                >
                                    {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                    {editingId ? 'Update' : 'Save Price'}
                                </Button>
                                {editingId && (
                                    <Button variant="outlined" size="small" onClick={handleCancelEdit}>
                                        Cancel
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>History</Typography>

                {loading && priceHistory.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : priceHistory.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No price history yet.</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Price</strong></TableCell>
                                    <TableCell><strong>From</strong></TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {priceHistory.map((log) => (
                                    <TableRow
                                        key={log.f_id}
                                        sx={editingId === log.f_id ? { bgcolor: 'primary.50' } : {}}
                                    >
                                        <TableCell>
                                            <strong>₹{log.f_price}</strong>
                                            {log.day_prices && Object.keys(log.day_prices).length > 0 && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    {Object.entries(log.day_prices)
                                                        .map(([d, p]) => `${DAY_NAMES[parseInt(d)]?.slice(0, 3)} ₹${p}`)
                                                        .join(' · ')}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {log.f_effective_from?.slice(0, 10)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleStartEdit(log)}
                                                    disabled={loading}
                                                    color={editingId === log.f_id ? 'primary' : 'default'}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(log.f_id)}
                                                    disabled={loading}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProductPriceDialog;
